import axios from 'axios';
import {
  API_BASE_URL,
  ADMIN_ENDPOINTS,
  API_CONFIG,
  USER_STATUS,
  SORT_OPTIONS,
  SORT_ORDER,
  EXPORT_FORMATS,
  TIMEFRAME_OPTIONS,
  getTokenFromStorage,
  STORAGE_KEYS
} from '../constant/constant';

// Create axios instance with default config
const createAdminApi = () => {
  const adminApi = axios.create({
    baseURL: `${API_BASE_URL}/admin`,
  });

  // Request interceptor to add auth token
  adminApi.interceptors.request.use(
    (config) => {
      const token = getTokenFromStorage();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor for error handling
  adminApi.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        // Token expired or invalid, clear Redux Persist storage and redirect to login
        localStorage.removeItem(STORAGE_KEYS.PERSIST_ROOT);
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
  );

  return adminApi;
};

const adminApi = createAdminApi();

// Helper function to handle API errors
const handleApiError = (error, defaultMessage) => {
  const errorMessage = error.response?.data?.msg || error.message || defaultMessage;
  throw new Error(errorMessage);
};

// Helper function to build query parameters
const buildQueryParams = (params) => {
  const queryParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, value.toString());
    }
  });

  return queryParams.toString();
};

// Admin API Service Functions
export const getAdminStats = async () => {
  try {
    const response = await adminApi.get(ADMIN_ENDPOINTS.STATS);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to fetch admin statistics');
  }
};

export const getUsers = async (params = {}) => {
  try {
    const {
      page = 1,
      limit = API_CONFIG.DEFAULT_PAGE_SIZE,
      search = '',
      status = USER_STATUS.ALL,
      sortBy = SORT_OPTIONS.CREATED_AT,
      sortOrder = SORT_ORDER.DESC
    } = params;

    const queryParams = buildQueryParams({
      page,
      limit,
      search,
      status,
      sortBy,
      sortOrder
    });

    const response = await adminApi.get(`${ADMIN_ENDPOINTS.USERS}?${queryParams}`);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to fetch users');
  }
};

export const getUser = async (userId) => {
  try {
    const response = await adminApi.get(ADMIN_ENDPOINTS.USER_DETAILS(userId));
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to fetch user details');
  }
};

export const updateUser = async (userId, userData) => {
  try {
    const response = await adminApi.put(ADMIN_ENDPOINTS.USER_UPDATE(userId), userData);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to update user');
  }
};

export const deleteUser = async (userId) => {
  try {
    const response = await adminApi.delete(ADMIN_ENDPOINTS.USER_DELETE(userId));
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to delete user');
  }
};

export const exportUsers = async (format = EXPORT_FORMATS.CSV, status = USER_STATUS.ALL) => {
  try {
    const queryParams = buildQueryParams({ format, status });
    debugger;
    const response = await adminApi.get(`${ADMIN_ENDPOINTS.USERS_EXPORT}?${queryParams}`, {
      responseType: 'blob'
    });

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `users-export-${new Date().toISOString().split('T')[0]}.${format}`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return { success: true };
  } catch (error) {
    handleApiError(error, 'Failed to export users');
  }
};

export const getAnalytics = async (timeframe = TIMEFRAME_OPTIONS.SEVEN_DAYS) => {
  try {
    const queryParams = buildQueryParams({ timeframe });
    const response = await adminApi.get(`${ADMIN_ENDPOINTS.ANALYTICS}?${queryParams}`);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to fetch analytics data');
  }
};

// Grant tokens to a user
export const grantTokens = async (userId, tokenData) => {
  try {
    const response = await adminApi.post(ADMIN_ENDPOINTS.GRANT_TOKENS(userId), tokenData);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to grant tokens');
  }
};

// Revoke tokens from a user
export const revokeTokens = async (userId, tokenData) => {
  try {
    const response = await adminApi.post(ADMIN_ENDPOINTS.REVOKE_TOKENS(userId), tokenData);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to revoke tokens');
  }
};

// Bulk grant tokens to multiple users
export const bulkGrantTokens = async (bulkTokenData) => {
  try {
    const response = await adminApi.post(ADMIN_ENDPOINTS.BULK_GRANT_TOKENS, bulkTokenData);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to bulk grant tokens');
  }
};

export const getLogs = async (params = {}) => {
  try {
    const {
      page = 1,
      limit = API_CONFIG.DEFAULT_PAGE_SIZE,
      status,
      endpoint,
      apiType,
      language,
      timeframe,
      userId,
      search
    } = params;

    const queryParams = buildQueryParams({
      page,
      limit,
      status,
      endpoint,
      apiType,
      language,
      timeframe,
      userId,
      search
    });

    const response = await adminApi.get(`${ADMIN_ENDPOINTS.LOGS}?${queryParams}`);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to fetch logs');
  }
};

export const exportLogs = async (params = {}) => {
  try {
    const {
      status,
      endpoint,
      apiType,
      language,
      timeframe,
      userId,
      search
    } = params;

    const queryParams = buildQueryParams({
      status,
      endpoint,
      apiType,
      language,
      timeframe,
      userId,
      search
    });

    const response = await adminApi.get(`${ADMIN_ENDPOINTS.LOGS_EXPORT}?${queryParams}`, {
      responseType: 'blob'
    });

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `api-usage-logs-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return { success: true };
  } catch (error) {
    handleApiError(error, 'Failed to export logs');
  }
};


export const getApiUsageAnalytics = async (params = {}) => {
  try {
    const queryParams = buildQueryParams(params);
    const url = queryParams ? `${ADMIN_ENDPOINTS.API_USAGE_ANALYTICS}?${queryParams}` : ADMIN_ENDPOINTS.API_USAGE_ANALYTICS;
    const response = await adminApi.get(url);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to fetch API usage analytics');
  }
};

// Export all functions as adminService object for backward compatibility
export const adminService = {
  getStats: getAdminStats,
  getUsers,
  getUser,
  updateUser,
  deleteUser,
  exportUsers,
  getAnalytics,
  getApiUsageAnalytics,
  grantTokens,
  revokeTokens,
  bulkGrantTokens,
  getLogs,
  exportLogs
};

export default adminService;